import { Box, Button, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { nanoid } from "nanoid"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { Sortable } from "lib/@components/Sortable"
import { If, Else } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useRefresh } from "lib/@hooks/useRefresh"
import { getDefaultRequestedFieldType } from "./requested-field-types"
import { RequestedFieldRow } from "./requested-field-row"

/**
 * Creator-facing builder for the "Requested information" a share link asks recipients to fill in.
 *
 * Reads/writes `requestedFields` on the bound share-link `target`. Adding, removing and reordering
 * mutate that array in place and call `refresh()` — because this component renders inside the share
 * dialog's `<Bound onChange={...}>`, `useRefresh()` captures that onChange, so every change both
 * re-renders the list and flows through the dialog's existing debounced save (no bespoke save here).
 * The server (`update-sharing-link.mjs` -> `normalizeRequestedFields`) re-validates on persist.
 */
export function RequestedFieldsEditor() {
    const context = useBoundContext()
    const refresh = useRefresh()
    const fields = getFields()

    return (
        <Box data-cy="requested-fields-editor">
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">Requested information</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        data-cy="requested-fields-add"
                        startIcon={<Iconify icon="mdi:plus" />}
                        onClick={addField}
                    >
                        Add field
                    </Button>
                </Stack>
                <If value={fields.length > 0}>
                    <Bound refresh={refresh}>
                        <Sortable items={fields} onOrdered={onReorder} id="id">
                            <Stack spacing={1}>
                                <Repeat list={fields} item={<RequestedFieldRow />} bind keyFn={fieldKey} />
                            </Stack>
                        </Sortable>
                    </Bound>
                    <Else>
                        <Typography variant="body2" color="text.secondary" data-cy="requested-fields-empty">
                            No information requested. Recipients will not be asked to complete any fields.
                        </Typography>
                    </Else>
                </If>
            </Stack>
        </Box>
    )

    function getFields() {
        if (!Array.isArray(context.target?.requestedFields)) return []
        return context.target.requestedFields
    }

    function addField() {
        if (!Array.isArray(context.target.requestedFields)) {
            context.target.requestedFields = []
        }
        const list = context.target.requestedFields
        list.push({
            id: nanoid(),
            type: getDefaultRequestedFieldType(),
            label: "",
            required: false,
            order: list.length,
        })
        reindex(list)
        refresh()
    }

    // `Sortable` has already reordered `requestedFields` in place by the time it calls back; we only
    // renumber `order` to match the new positions and refresh (which flows through the dialog's save).
    function onReorder() {
        const list = context.target.requestedFields
        if (!Array.isArray(list)) return
        reindex(list)
        refresh()
    }

    function fieldKey(field, index) {
        return field?.id ?? index
    }
}

function reindex(list) {
    list.forEach((field, index) => {
        field.order = index
    })
}
